/**
 * KRP Design System - "Modern Corporate Precision"
 *
 * A comprehensive design system for the KRP Equipment Rental Management System.
 * Designed for Thai language support with professional, trustworthy aesthetics.
 *
 * @version 2.0.0
 * @author Claude Design System
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

/**
 * Primary Color: Deep Navy Blue
 * Conveys: Trust, Authority, Professionalism
 * Matches tailwind.config.js "Executive Navy" palette (source of truth)
 */
export const colors = {
  // Primary - Deep Navy Blue (Main brand color - synced with tailwind.config.js)
  primary: {
    50: '#f0f5fa',   // Lightest - backgrounds
    100: '#dae6f2',  // Light backgrounds
    200: '#b8cfe6',  // Hover states (light)
    300: '#8cb2d6',  // Borders
    400: '#5a8fc2',  // Icons, accents
    500: '#3d72ab',  // Default buttons, links
    600: '#2d5a8f',  // Hover buttons
    700: '#1e3a5f',  // Active/pressed
    800: '#162c48',  // Text on light bg
    900: '#0f2744',  // Headings
    950: '#091a2e',  // Darkest - dark mode bg
  },

  // Secondary - Warm Slate (Neutral with warmth)
  secondary: {
    50: '#f9fafb',   // Page backgrounds
    100: '#F1F5F9',  // Card backgrounds
    200: '#E2E8F0',  // Borders, dividers
    300: '#CBD5E1',  // Disabled states
    400: '#94A3B8',  // Placeholder text
    500: '#64748B',  // Secondary text
    600: '#475569',  // Body text
    700: '#334155',  // Primary text
    800: '#1E293B',  // Headings
    900: '#0F172A',  // Dark backgrounds
    950: '#020617',  // Darkest
  },

  // Success - Forest Teal (Growth + Reliability) — synced with tailwind.config.js
  success: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',  // Default
    600: '#0d9488',  // Hover
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
    950: '#042f2e',
  },

  // Warning - Amber (High visibility)
  warning: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',  // Default
    600: '#D97706',  // Hover
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
    950: '#451A03',
  },

  // Danger - Rose (Clear but not aggressive)
  danger: {
    50: '#FFF1F2',
    100: '#FFE4E6',
    200: '#FECDD3',
    300: '#FDA4AF',
    400: '#FB7185',
    500: '#F43F5E',  // Default
    600: '#E11D48',  // Hover
    700: '#BE123C',
    800: '#9F1239',
    900: '#881337',
    950: '#4C0519',
  },

  // Info - Sky Blue (Informational, calm)
  info: {
    50: '#F0F9FF',
    100: '#E0F2FE',
    200: '#BAE6FD',
    300: '#7DD3FC',
    400: '#38BDF8',
    500: '#0EA5E9',  // Default
    600: '#0284C7',  // Hover
    700: '#0369A1',
    800: '#075985',
    900: '#0C4A6E',
    950: '#082F49',
  },

  // Accent - Violet (For special highlights, charts)
  accent: {
    50: '#F5F3FF',
    100: '#EDE9FE',
    200: '#DDD6FE',
    300: '#C4B5FD',
    400: '#A78BFA',
    500: '#8B5CF6',  // Default
    600: '#7C3AED',  // Hover
    700: '#6D28D9',
    800: '#5B21B6',
    900: '#4C1D95',
    950: '#2E1065',
  },

  // Pure colors
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

/**
 * Typography Scale
 * Optimized for Thai language (Kanit + Sarabun)
 * Thai text needs slightly larger line-height for readability
 */
export const typography = {
  // Font families
  fontFamily: {
    sans: ['Kanit', 'Sarabun', '-apple-system', 'BlinkMacSystemFont', 'sans-serif'],
    display: ['Kanit', 'sans-serif'],      // For headings
    body: ['Sarabun', 'sans-serif'],       // For body text
    mono: ['JetBrains Mono', 'Fira Code', 'monospace'],
  },

  // Font sizes with line heights optimized for Thai
  fontSize: {
    '2xs': ['0.625rem', { lineHeight: '1rem' }],      // 10px
    xs: ['0.75rem', { lineHeight: '1.125rem' }],      // 12px
    sm: ['0.8125rem', { lineHeight: '1.25rem' }],     // 13px - Thai optimized
    base: ['0.9375rem', { lineHeight: '1.5rem' }],    // 15px - Thai optimized
    lg: ['1.0625rem', { lineHeight: '1.625rem' }],    // 17px
    xl: ['1.25rem', { lineHeight: '1.75rem' }],       // 20px
    '2xl': ['1.5rem', { lineHeight: '2rem' }],        // 24px
    '3xl': ['1.875rem', { lineHeight: '2.25rem' }],   // 30px
    '4xl': ['2.25rem', { lineHeight: '2.5rem' }],     // 36px
    '5xl': ['3rem', { lineHeight: '1.15' }],          // 48px
    '6xl': ['3.75rem', { lineHeight: '1.1' }],        // 60px
  },

  // Font weights
  fontWeight: {
    thin: '100',
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  // Letter spacing
  letterSpacing: {
    tighter: '-0.02em',
    tight: '-0.01em',
    normal: '0',
    wide: '0.01em',
    wider: '0.02em',
  },
} as const;

// ============================================================================
// SPACING
// ============================================================================

/**
 * Spacing Scale
 * Based on 4px grid system
 */
export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',   // 2px
  1: '0.25rem',      // 4px
  1.5: '0.375rem',   // 6px
  2: '0.5rem',       // 8px
  2.5: '0.625rem',   // 10px
  3: '0.75rem',      // 12px
  3.5: '0.875rem',   // 14px
  4: '1rem',         // 16px
  5: '1.25rem',      // 20px
  6: '1.5rem',       // 24px
  7: '1.75rem',      // 28px
  8: '2rem',         // 32px
  9: '2.25rem',      // 36px
  10: '2.5rem',      // 40px
  11: '2.75rem',     // 44px
  12: '3rem',        // 48px
  14: '3.5rem',      // 56px
  16: '4rem',        // 64px
  20: '5rem',        // 80px
  24: '6rem',        // 96px
  28: '7rem',        // 112px
  32: '8rem',        // 128px
} as const;

// ============================================================================
// BORDER RADIUS
// ============================================================================

/**
 * Border Radius Scale
 * Slightly larger radii for modern, friendly feel
 */
export const borderRadius = {
  none: '0',
  sm: '0.25rem',     // 4px - Small elements
  DEFAULT: '0.375rem', // 6px - Inputs, small buttons
  md: '0.5rem',      // 8px - Cards, buttons
  lg: '0.75rem',     // 12px - Large cards
  xl: '1rem',        // 16px - Modals
  '2xl': '1.25rem',  // 20px - Large modals
  '3xl': '1.5rem',   // 24px - Feature cards
  full: '9999px',    // Pills, avatars
} as const;

// ============================================================================
// SHADOWS
// ============================================================================

/**
 * Shadow Scale
 * Refined shadows with subtle warmth
 */
export const shadows = {
  // Elevated shadows
  none: 'none',
  xs: '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
  sm: '0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.06)',
  DEFAULT: '0 2px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.06)',
  md: '0 4px 8px -2px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.04)',
  lg: '0 8px 16px -4px rgba(15, 23, 42, 0.1), 0 4px 8px -4px rgba(15, 23, 42, 0.06)',
  xl: '0 16px 32px -8px rgba(15, 23, 42, 0.12), 0 8px 16px -8px rgba(15, 23, 42, 0.08)',
  '2xl': '0 24px 48px -12px rgba(15, 23, 42, 0.16)',

  // Specific component shadows
  card: '0 2px 8px -2px rgba(15, 23, 42, 0.06), 0 4px 12px -4px rgba(15, 23, 42, 0.04)',
  'card-hover': '0 4px 16px -4px rgba(15, 23, 42, 0.1), 0 8px 24px -8px rgba(15, 23, 42, 0.08)',
  modal: '0 20px 40px -12px rgba(15, 23, 42, 0.2), 0 8px 16px -8px rgba(15, 23, 42, 0.1)',
  dropdown: '0 4px 12px -2px rgba(15, 23, 42, 0.12), 0 2px 6px -2px rgba(15, 23, 42, 0.08)',
  navbar: '0 1px 3px 0 rgba(15, 23, 42, 0.06), 0 1px 2px -1px rgba(15, 23, 42, 0.04)',
  sidebar: '4px 0 12px -4px rgba(15, 23, 42, 0.08)',
  input: '0 1px 2px 0 rgba(15, 23, 42, 0.04)',
  'input-focus': '0 0 0 3px rgba(30, 58, 95, 0.15)',
  button: '0 1px 2px 0 rgba(15, 23, 42, 0.05)',
  'button-hover': '0 2px 4px 0 rgba(15, 23, 42, 0.08)',

  // Colored shadows for buttons
  'primary': '0 4px 12px -2px rgba(30, 58, 95, 0.35)',
  'success': '0 4px 12px -2px rgba(16, 185, 129, 0.3)',
  'danger': '0 4px 12px -2px rgba(244, 63, 94, 0.3)',
  'warning': '0 4px 12px -2px rgba(245, 158, 11, 0.3)',

  // Inner shadow
  inner: 'inset 0 2px 4px 0 rgba(15, 23, 42, 0.05)',
} as const;

// ============================================================================
// TRANSITIONS
// ============================================================================

/**
 * Transition Presets
 */
export const transitions = {
  // Durations
  duration: {
    fastest: '50ms',
    fast: '100ms',
    normal: '150ms',
    slow: '200ms',
    slower: '300ms',
    slowest: '500ms',
  },

  // Easings
  easing: {
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Presets
  presets: {
    default: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    fast: 'all 100ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    colors: 'color, background-color, border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    transform: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: 'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1)',
    shadow: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// ============================================================================
// Z-INDEX SCALE
// ============================================================================

export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// ============================================================================
// COMPONENT TOKENS
// ============================================================================

/**
 * Component-specific design tokens
 */
export const components = {
  // Button variants
  button: {
    // Sizes
    size: {
      xs: {
        height: '1.75rem',    // 28px
        padding: '0 0.625rem', // px-2.5
        fontSize: '0.75rem',   // text-xs
        borderRadius: '0.25rem',
      },
      sm: {
        height: '2rem',        // 32px
        padding: '0 0.75rem',  // px-3
        fontSize: '0.8125rem', // text-sm
        borderRadius: '0.375rem',
      },
      md: {
        height: '2.5rem',      // 40px
        padding: '0 1rem',     // px-4
        fontSize: '0.875rem',  // text-sm
        borderRadius: '0.5rem',
      },
      lg: {
        height: '2.75rem',     // 44px
        padding: '0 1.25rem',  // px-5
        fontSize: '1rem',      // text-base
        borderRadius: '0.5rem',
      },
      xl: {
        height: '3rem',        // 48px
        padding: '0 1.5rem',   // px-6
        fontSize: '1.0625rem', // text-lg
        borderRadius: '0.625rem',
      },
    },
    // Variants
    variant: {
      primary: {
        bg: colors.primary[600],
        bgHover: colors.primary[700],
        text: colors.white,
        border: 'transparent',
        shadow: shadows.primary,
      },
      secondary: {
        bg: colors.secondary[100],
        bgHover: colors.secondary[200],
        text: colors.secondary[700],
        border: colors.secondary[200],
        shadow: 'none',
      },
      outline: {
        bg: 'transparent',
        bgHover: colors.primary[50],
        text: colors.primary[600],
        border: colors.primary[300],
        shadow: 'none',
      },
      ghost: {
        bg: 'transparent',
        bgHover: colors.secondary[100],
        text: colors.secondary[600],
        border: 'transparent',
        shadow: 'none',
      },
      danger: {
        bg: colors.danger[600],
        bgHover: colors.danger[700],
        text: colors.white,
        border: 'transparent',
        shadow: shadows.danger,
      },
      success: {
        bg: colors.success[600],
        bgHover: colors.success[700],
        text: colors.white,
        border: 'transparent',
        shadow: shadows.success,
      },
    },
  },

  // Input variants
  input: {
    size: {
      sm: {
        height: '2rem',
        padding: '0 0.625rem',
        fontSize: '0.8125rem',
      },
      md: {
        height: '2.5rem',
        padding: '0 0.75rem',
        fontSize: '0.875rem',
      },
      lg: {
        height: '3rem',
        padding: '0 1rem',
        fontSize: '1rem',
      },
    },
    state: {
      default: {
        bg: colors.white,
        border: colors.secondary[300],
        text: colors.secondary[800],
        placeholder: colors.secondary[400],
      },
      focus: {
        border: colors.primary[500],
        ring: `0 0 0 3px ${colors.primary[500]}20`,
      },
      error: {
        border: colors.danger[500],
        ring: `0 0 0 3px ${colors.danger[500]}20`,
      },
      disabled: {
        bg: colors.secondary[100],
        border: colors.secondary[200],
        text: colors.secondary[400],
      },
    },
  },

  // Card variants
  card: {
    default: {
      bg: colors.white,
      border: colors.secondary[200],
      shadow: shadows.card,
      borderRadius: borderRadius.lg,
    },
    elevated: {
      bg: colors.white,
      border: 'transparent',
      shadow: shadows.md,
      borderRadius: borderRadius.lg,
    },
    outlined: {
      bg: 'transparent',
      border: colors.secondary[200],
      shadow: 'none',
      borderRadius: borderRadius.lg,
    },
  },

  // Modal
  modal: {
    backdrop: 'rgba(15, 23, 42, 0.5)',
    bg: colors.white,
    shadow: shadows.modal,
    borderRadius: borderRadius.xl,
    size: {
      sm: '24rem',    // 384px
      md: '32rem',    // 512px
      lg: '42rem',    // 672px
      xl: '56rem',    // 896px
      full: '100%',
    },
  },

  // Table
  table: {
    header: {
      bg: colors.secondary[50],
      text: colors.secondary[600],
      fontWeight: '600',
    },
    row: {
      bg: colors.white,
      bgHover: colors.secondary[50],
      bgStripe: colors.secondary[50],
      border: colors.secondary[200],
    },
  },

  // Sidebar
  sidebar: {
    width: '280px',
    collapsedWidth: '64px',
    bg: colors.secondary[900],
    text: colors.secondary[300],
    textHover: colors.white,
    activeItem: {
      bg: colors.primary[600],
      text: colors.white,
    },
  },

  // Navbar
  navbar: {
    height: '64px',
    bg: colors.white,
    border: colors.secondary[200],
    shadow: shadows.navbar,
  },
} as const;

// ============================================================================
// STATUS COLORS
// ============================================================================

/**
 * Unified Status Color Configuration
 * Use this for all status badges across the application
 */
export const statusColors = {
  // Invoice Status
  invoice: {
    DRAFT: {
      label: 'แบบร่าง',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-500',
    },
    SENT: {
      label: 'ส่งแล้ว',
      bg: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
      dot: 'bg-info-500',
    },
    PAID: {
      label: 'ชำระแล้ว',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    OVERDUE: {
      label: 'เกินกำหนด',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    CANCELLED: {
      label: 'ยกเลิก',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-400',
    },
    PARTIALLY_PAID: {
      label: 'ชำระบางส่วน',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
  },

  // Payment Status
  payment: {
    PENDING: {
      // D2 (MANUAL_AUDIT_SYSTEM_FINDINGS): เดิม badge เขียน "รอดำเนินการ" แต่ตัวกรองใน
      // หน้าเดียวกัน (PaymentsPage) เขียน "รอยืนยัน" → ผู้ใช้กรอง "รอยืนยัน" แล้วเจอแถวที่
      // ป้ายเขียน "รอดำเนินการ" · เลือกใช้ "รอยืนยัน" ทั้งระบบเพราะตรงกับ flow จริง
      // (PENDING → CONFIRMED = รอคนยืนยันการรับเงิน) และตรงกับ backend อยู่แล้ว
      // (models/payment.py:10 comment, services/pdf_service.py:385 ที่พิมพ์ลงใบเสร็จ)
      label: 'รอยืนยัน',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    CONFIRMED: {
      label: 'ยืนยันแล้ว',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    REFUNDED: {
      label: 'คืนเงินแล้ว',
      bg: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
      dot: 'bg-info-500',
    },
    FAILED: {
      label: 'ล้มเหลว',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    CANCELLED: {
      label: 'ยกเลิก',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-400',
    },
  },

  // Quotation Status
  quotation: {
    DRAFT: {
      label: 'แบบร่าง',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-500',
    },
    SENT: {
      label: 'ส่งแล้ว',
      bg: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
      dot: 'bg-info-500',
    },
    ACCEPTED: {
      label: 'ยอมรับแล้ว',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    REJECTED: {
      label: 'ปฏิเสธแล้ว',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    EXPIRED: {
      label: 'หมดอายุ',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    CONVERTED: {
      label: 'สร้างรายการเช่าแล้ว',
      bg: 'bg-primary-100',
      text: 'text-primary-700',
      border: 'border-primary-200',
      dot: 'bg-primary-500',
    },
  },

  // Rental Status
  rental: {
    ACTIVE: {
      label: 'เช่าใช้อยู่',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    DRAFT: {
      label: 'ร่าง',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    PENDING: {
      label: 'รอดำเนินการ',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    PENDING_CANCELLATION: {
      label: 'รอการยกเลิก',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    EXPIRED: {
      label: 'หมดอายุ',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    CANCELLED: {
      label: 'ยกเลิกแล้ว',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-400',
    },
    COMPLETED: {
      label: 'เสร็จสิ้น',
      bg: 'bg-primary-100',
      text: 'text-primary-700',
      border: 'border-primary-200',
      dot: 'bg-primary-500',
    },
  },

  // Maintenance Status
  // สีสถานะซ่อมบำรุง: เลือกจาก palette semantic ของ KRP ให้แยกเฉดชัด (สแกนคอลัมน์สถานะง่าย)
  //   เปิด=แดง(danger) · รอดำเนินการ=เหลือง(warning) · กำลังติดตาม=ฟ้า(info) · ปิด=เขียว(success) · ยกเลิก=เทา(secondary)
  maintenance: {
    OPEN: {
      label: 'เปิด',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    PENDING: {
      label: 'รอดำเนินการ',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    FOLLOW_UP: {
      // D1 (MANUAL_AUDIT_SYSTEM_FINDINGS): badge/ตัวกรองเคยเขียน "รอติดตาม" แต่ dropdown
      // ในฟอร์มเขียน "กำลังติดตาม" · เลือก "กำลังติดตาม" ทั้งระบบ เพราะ "รอติดตาม" ซ้ำซ้อน
      // กับ PENDING = "รอดำเนินการ" ที่อยู่ติดกันใน dropdown เดียวกันจนแยกไม่ออก และ
      // FOLLOW_UP เป็นสถานะที่ "กำลังทำอยู่" จริง (นับรวมเป็นเคสค้างกับ OPEN/PENDING)
      label: 'กำลังติดตาม',
      bg: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
      dot: 'bg-info-500',
    },
    CLOSED: {
      label: 'ปิด',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    CANCELLED: {
      label: 'ยกเลิก',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-400',
    },
  },

  // Contract Status
  contract: {
    DRAFT: {
      label: 'ร่างสัญญา',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-500',
    },
    ACTIVE: {
      label: 'มีผลบังคับใช้',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    EXPIRED: {
      label: 'หมดอายุ',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    TERMINATED: {
      label: 'ยกเลิกสัญญา',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
  },

  // Device Status
  device: {
    AVAILABLE: {
      label: 'พร้อมใช้งาน',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    RENTED: {
      label: 'กำลังใช้งาน',
      bg: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
      dot: 'bg-info-500',
    },
    // Phase 2 คลังอุปกรณ์ (PO 2026-09-07): ของถูกเบิก/ยืมไปใช้ "ภายใน" ไม่ใช่ให้ลูกค้าเช่า
    // — ต่างจาก RENTED ตรงที่ไม่มีสัญญา/รายได้ผูกอยู่ จึงต้องแยกสีให้เห็นทันทีในตารางอุปกรณ์
    ISSUED: {
      label: 'เบิกไปใช้ภายใน',
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      dot: 'bg-purple-500',
    },
    // ใบงานเตรียมเครื่อง (PO 2026-09-08): กันของไว้ให้ดีลที่ปิดแล้ว — ยังอยู่ในคลัง ยังไม่ส่งมอบ
    // ใช้โทน accent (ทองแดง) ไม่ใช่ info เหมือน RENTED เพราะ "จองไว้" ยังไม่ใช่ "ลูกค้าถืออยู่"
    // — ถ้าใช้สีเดียวกันคนดูตารางจะนับรวมเป็นเครื่องที่ส่งมอบแล้วทันที
    // ป้ายไทยตรงกับ backend `DEVICE_STATUS_TH['RESERVED']` เป๊ะ ๆ (export/API แสดงคำเดียวกัน)
    RESERVED: {
      label: 'จองไว้ (ใบงานเตรียมเครื่อง)',
      bg: 'bg-accent-100',
      text: 'text-accent-700',
      border: 'border-accent-200',
      dot: 'bg-accent-500',
    },
    MAINTENANCE: {
      label: 'ซ่อมบำรุง',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    DAMAGED: {
      label: 'ชำรุด',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    LOST: {
      label: 'สูญหาย',
      bg: 'bg-secondary-800',
      text: 'text-white',
      border: 'border-secondary-800',
      dot: 'bg-white',
    },
    DISPOSED: {
      label: 'จำหน่ายออก',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-400',
    },
  },

  // Generic statuses
  generic: {
    active: {
      label: 'ใช้งาน',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    inactive: {
      label: 'ไม่ใช้งาน',
      bg: 'bg-secondary-100',
      text: 'text-secondary-700',
      border: 'border-secondary-200',
      dot: 'bg-secondary-400',
    },
    pending: {
      label: 'รอดำเนินการ',
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      border: 'border-warning-200',
      dot: 'bg-warning-500',
    },
    success: {
      label: 'สำเร็จ',
      bg: 'bg-success-100',
      text: 'text-success-700',
      border: 'border-success-200',
      dot: 'bg-success-500',
    },
    error: {
      label: 'ผิดพลาด',
      bg: 'bg-danger-100',
      text: 'text-danger-700',
      border: 'border-danger-200',
      dot: 'bg-danger-500',
    },
    info: {
      label: 'ข้อมูล',
      bg: 'bg-info-100',
      text: 'text-info-700',
      border: 'border-info-200',
      dot: 'bg-info-500',
    },
  },
} as const;

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type ColorScale = keyof typeof colors.primary;
export type StatusType = keyof typeof statusColors;
export type ButtonSize = keyof typeof components.button.size;
export type ButtonVariant = keyof typeof components.button.variant;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get status badge classes
 */
export function getStatusClasses(
  type: keyof typeof statusColors,
  status: string
): { bg: string; text: string; border: string; dot: string; label: string } {
  const statusConfig = statusColors[type] as Record<string, any>;
  const config = statusConfig[status] || statusColors.generic.inactive;
  return config;
}

/**
 * Combine class names (like clsx/cn)
 */
export function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ============================================================================
// CSS VARIABLES (for runtime theming)
// ============================================================================

export const cssVariables = `
  :root {
    /* Primary */
    --color-primary-50: ${colors.primary[50]};
    --color-primary-100: ${colors.primary[100]};
    --color-primary-200: ${colors.primary[200]};
    --color-primary-300: ${colors.primary[300]};
    --color-primary-400: ${colors.primary[400]};
    --color-primary-500: ${colors.primary[500]};
    --color-primary-600: ${colors.primary[600]};
    --color-primary-700: ${colors.primary[700]};
    --color-primary-800: ${colors.primary[800]};
    --color-primary-900: ${colors.primary[900]};
    --color-primary-950: ${colors.primary[950]};

    /* Secondary */
    --color-secondary-50: ${colors.secondary[50]};
    --color-secondary-100: ${colors.secondary[100]};
    --color-secondary-200: ${colors.secondary[200]};
    --color-secondary-300: ${colors.secondary[300]};
    --color-secondary-400: ${colors.secondary[400]};
    --color-secondary-500: ${colors.secondary[500]};
    --color-secondary-600: ${colors.secondary[600]};
    --color-secondary-700: ${colors.secondary[700]};
    --color-secondary-800: ${colors.secondary[800]};
    --color-secondary-900: ${colors.secondary[900]};
    --color-secondary-950: ${colors.secondary[950]};

    /* Semantic */
    --color-success: ${colors.success[500]};
    --color-warning: ${colors.warning[500]};
    --color-danger: ${colors.danger[500]};
    --color-info: ${colors.info[500]};

    /* Shadows */
    --shadow-card: ${shadows.card};
    --shadow-modal: ${shadows.modal};
    --shadow-dropdown: ${shadows.dropdown};

    /* Transitions */
    --transition-fast: ${transitions.presets.fast};
    --transition-default: ${transitions.presets.default};
    --transition-slow: ${transitions.presets.slow};
  }

  /* Dark mode (future support) */
  .dark {
    --color-primary-50: ${colors.primary[950]};
    --color-primary-100: ${colors.primary[900]};
    --color-primary-200: ${colors.primary[800]};
    --color-secondary-50: ${colors.secondary[950]};
    --color-secondary-100: ${colors.secondary[900]};
    --color-secondary-200: ${colors.secondary[800]};
  }
`;

export default {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  components,
  statusColors,
  getStatusClasses,
  cn,
};
